import { useState } from "react";
import { Zap, Users, Calendar, CheckCircle2, ArrowRight, MessageSquareText, Home, Sparkles, Baby, ClipboardList, BellRing, ChevronDown, MessageCircleOff } from "lucide-react";

const FAQS = [
  {
    q: "Cos'è EASY Family?",
    a: "EASY Family è l'app della tua Casa: raccoglie in un posto solo la disponibilità dei familiari, i ritrovi, le liste condivise e i promemoria. Zero vocali di 3 minuti, tutto in un click. È una Casa dell'ecosistema ZapHQ.space.",
  },
  {
    q: "Cosa vuol dire \"Facciamo uno Zap?\"",
    a: "Uno Zap è il momento in cui incrociamo i Google Calendar di chi vuoi coinvolgere e troviamo istantaneamente lo slot in cui siete tutti liberi. Come Booking.com, ma la 'camera' è la disponibilità dei tuoi cari. Poi crea l'evento su Google Calendar di tutti + un QR / link WhatsApp da mandare al gruppo.",
  },
  {
    q: "EASY Family è una Casa di ZapHQ.space?",
    a: "Sì. EASY Family è la Casa dedicata alla famiglia dentro l'ecosistema ZapHQ.space. Puoi passare dalla Reception ZapHQ (◉ in alto) per scoprire le altre Case (lavoro, amici, sport, ecc.).",
  },
  {
    q: "Come accedo?",
    a: "Tre modi: (1) email + password personale se hai già un account, (2) codice invito ZAP-XXXXX ricevuto via WhatsApp dal capofamiglia, (3) Accedi con Google. Se sei il primo, clicca 'Sono il capofamiglia' e usa il codice della Casa.",
  },
  {
    q: "Devo installare qualcosa?",
    a: "No. EASY Family funziona nel browser (Safari, Chrome, Firefox). Se vuoi, dal menu del telefono puoi 'Aggiungi a schermata Home' e sembrerà un'app vera (PWA). Niente store, niente aggiornamenti da scaricare.",
  },
];

function FAQItem({ q, a, idx }) {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] transition hover:bg-white/[0.05]">
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
        data-testid={`faq-toggle-${idx}`}
      >
        <span className="text-base font-semibold text-white">{q}</span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-[#F5B942] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="border-t border-white/10 px-5 py-4 text-sm leading-relaxed text-white/70">
          {a}
        </div>
      )}
    </div>
  );
}

export default function WelcomePage({ onLogin }) {
  return (
    <div data-testid="welcome-page" className="min-h-screen bg-[#0e0820] text-white">
      {/* Ambient glows */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 h-[500px] w-[500px] rounded-full bg-[#8B5CF6]/20 blur-[120px]" />
        <div className="absolute top-1/3 -left-40 h-[400px] w-[400px] rounded-full bg-[#F5B942]/15 blur-[120px]" />
        <div className="absolute bottom-0 right-1/4 h-[400px] w-[400px] rounded-full bg-[#2DD4BF]/10 blur-[120px]" />
      </div>

      <div className="relative mx-auto max-w-5xl px-6 py-12 sm:py-16">
        {/* HERO */}
        <section className="text-center">
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#F5B942]/30 bg-[#F5B942]/10 px-4 py-1.5">
            <Home className="h-3 w-3 text-[#F5B942]" />
            <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#F5B942]">
              Casa XII · Famiglia
            </span>
          </div>

          <h1 className="font-display text-5xl sm:text-6xl md:text-7xl font-black leading-[0.95] tracking-tight text-white">
            Facciamo uno <span className="text-[#F5B942] italic">Zap!?</span>
          </h1>
          <p className="mx-auto mt-3 max-w-md font-display text-lg sm:text-xl italic text-[#C084FC]">
            · EASY Family ·
          </p>

          <h2 className="mx-auto mt-8 max-w-2xl text-lg sm:text-xl font-medium leading-relaxed text-white/80">
            La famiglia si organizza in un attimo:{" "}
            <span className="text-[#2DD4BF]">pranzi, cene e ritrovi</span>,{" "}
            senza vocali da tre minuti.
          </h2>

          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              data-testid="welcome-login-cta"
              onClick={onLogin}
              className="group inline-flex items-center gap-2 rounded-full bg-[#F5B942] px-8 py-4 text-base font-bold uppercase tracking-wider text-[#1a1030] shadow-[0_0_40px_-8px_#F5B942] transition hover:scale-105 active:scale-95"
            >
              <Zap className="h-5 w-5" fill="#1a1030" />
              Entra / Accedi
              <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
            </button>
            <a
              href="#come-funziona"
              className="text-xs font-semibold text-white/60 hover:text-white underline-offset-4 hover:underline"
            >
              Come funziona? ↓
            </a>
          </div>
        </section>

        {/* PAIN — Il vocale di 3 minuti */}
        <section className="mt-24 sm:mt-32">
          <div className="mx-auto max-w-3xl rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.04] to-transparent p-8 sm:p-12">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-red-500/10 text-red-400">
                <MessageCircleOff className="h-6 w-6" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-red-400">Il problema</p>
                <p
                  className="mt-3 text-3xl sm:text-4xl leading-tight text-white/90"
                  style={{ fontFamily: "'Dancing Script', cursive" }}
                >
                  "Aspetta che ti mando un vocale…"
                </p>
                <p className="mt-4 text-sm sm:text-base leading-relaxed text-white/70">
                  Tre minuti di audio di Zia per decidere che <em>forse</em> domenica ci si vede.
                  Poi mamma non riesce, papà non l'ha sentito, e il pranzo salta. Ogni. Singola. Volta.
                </p>
                <p className="mt-3 text-sm font-semibold text-[#F5B942]">
                  ⚡ EASY Family lo risolve in un click.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* COME FUNZIONA */}
        <section id="come-funziona" className="mt-24 sm:mt-32">
          <div className="text-center">
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#2DD4BF]">Come funziona uno Zap</p>
            <h3 className="mt-3 font-display text-3xl sm:text-4xl font-bold">Tre passi, cinque secondi.</h3>
          </div>

          <div className="mt-10 grid gap-4 sm:grid-cols-3">
            {[
              { icon: Users, num: "01", title: "Chi inviti", body: "Spunta i familiari dalla lista della Casa. Nonna, cugino Marco, mamma. Chi vuoi tu." },
              { icon: Calendar, num: "02", title: "Quando", body: "Range di date + durata. L'app legge i Google Calendar di tutti e trova gli slot liberi." },
              { icon: CheckCircle2, num: "03", title: "Scegli lo slot", body: "Un click sullo slot vincente → evento creato su Calendar di tutti + QR + WhatsApp." },
            ].map((s, i) => (
              <div key={i} className="relative rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.04] to-transparent p-6 transition hover:border-[#F5B942]/40 hover:bg-white/[0.06]">
                <div className="absolute -top-3 left-6 rounded-full bg-[#F5B942] px-3 py-0.5 text-[10px] font-bold tracking-wider text-[#1a1030]">
                  {s.num}
                </div>
                <s.icon className="h-8 w-8 text-[#C084FC]" />
                <h4 className="mt-4 text-lg font-bold text-white">{s.title}</h4>
                <p className="mt-2 text-sm leading-relaxed text-white/70">{s.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* BENEFICI */}
        <section className="mt-24 sm:mt-32">
          <div className="text-center">
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#F5B942]">Perché ti piacerà</p>
            <h3 className="mt-3 font-display text-3xl sm:text-4xl font-bold">Fatta per la <em className="text-[#F5B942]">tua</em> famiglia.</h3>
          </div>

          <div className="mt-10 grid gap-3 sm:grid-cols-2">
            {[
              { icon: MessageCircleOff, title: "Zero vocali infiniti", body: "Basta audio da tre minuti. L'app dice quando siamo tutti liberi. Punto." },
              { icon: Baby, title: "Anche i nonni ci riescono", body: "Interfaccia zia-friendly, testo grande, un pulsante per ogni cosa. Nessun tutorial." },
              { icon: ClipboardList, title: "Liste condivise", body: "Cosa portare, chi cucina, cosa comprare. Tutti aggiornati in tempo reale." },
              { icon: BellRing, title: "Promemoria automatici", body: "Nessuno si dimentica: Google Calendar avvisa tutti col tempismo giusto." },
            ].map((b, i) => (
              <div key={i} className="flex items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-4 transition hover:bg-white/[0.06]">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#2DD4BF]/10 text-[#2DD4BF]">
                  <b.icon className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <p className="font-bold text-white">{b.title}</p>
                  <p className="mt-1 text-sm text-white/70">{b.body}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="mt-24 sm:mt-32">
          <div className="text-center">
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#C084FC]">Domande frequenti</p>
            <h3 className="mt-3 font-display text-3xl sm:text-4xl font-bold">Le cose che ci chiedete.</h3>
          </div>
          <div className="mt-8 space-y-3">
            {FAQS.map((f, i) => (
              <FAQItem key={i} idx={i} q={f.q} a={f.a} />
            ))}
          </div>
        </section>

        {/* CTA FINALE */}
        <section className="mt-24 sm:mt-32 text-center">
          <div className="mx-auto max-w-2xl rounded-3xl border border-[#F5B942]/30 bg-gradient-to-br from-[#F5B942]/10 via-transparent to-[#8B5CF6]/10 p-10">
            <Sparkles className="mx-auto h-8 w-8 text-[#F5B942]" />
            <h3 className="mt-4 font-display text-3xl font-bold">
              Pronti a fare uno <span className="text-[#F5B942] italic">Zap</span>?
            </h3>
            <p className="mt-3 text-sm text-white/70">
              Entra, collega Google Calendar, invita i tuoi cari. Il resto lo facciamo noi.
            </p>
            <button
              data-testid="welcome-login-cta-bottom"
              onClick={onLogin}
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#F5B942] px-8 py-4 text-base font-bold uppercase tracking-wider text-[#1a1030] shadow-[0_0_40px_-8px_#F5B942] transition hover:scale-105 active:scale-95"
            >
              <Zap className="h-5 w-5" fill="#1a1030" />
              Entra / Accedi
            </button>
            <p className="mt-4 text-[11px] text-white/50">
              Email + password · Codice ZAP-XXXXX · Accedi con Google
            </p>
          </div>
        </section>

        {/* FOOTER */}
        <footer className="mt-20 flex flex-col items-center gap-2 border-t border-white/10 pt-8 text-center">
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#F5B942]/70">
            Fatto con amore da ZapHQ.space
          </p>
          <a
            href="https://www.zaphq.space/zaphq"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[11px] text-white/50 hover:text-white/80"
          >
            ◉ Torna alla Reception dell'ecosistema
          </a>
        </footer>
      </div>
    </div>
  );
}
